"use client";

import React from "react";
import { useRouter, usePathname } from "next/navigation";
import { Loader2, CheckCircle2, AlertCircle, X, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { cn } from "@/app/_module/lib/utils";
import { useRouteProgress, RouteJob, RouteJobStage } from "../contexts/RouteProgressContext";

interface RouteProgressWidgetProps {
  // Same offset hooks the import widget uses — let positioning compose
  // with active-call docks / SMS panels so cards never overlap.
  hasActiveCall?: boolean;
  hasActiveSms?: boolean;
}

// Normalize the per-stage progress shown in the bar. Real backend
// progress isn't streamed (the optimize endpoint is one shot), so we
// pick deterministic anchor values per stage and nudge a smooth
// in-stage tween via setInterval inside the card.
const STAGE_ANCHOR: Record<RouteJobStage, number> = {
  queued: 5,
  geocoding: 25,
  optimizing: 60,
  rendering: 85,
  completing: 100,
  completed: 100,
  failed: 0,
};

const formatDistance = (m?: number) => {
  if (!m || !Number.isFinite(m)) return null;
  const miles = m / 1609.34;
  if (miles < 0.1) return `${Math.round(m * 3.281)} ft`;
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
};

const formatDuration = (s?: number) => {
  if (!s || !Number.isFinite(s)) return null;
  const mins = Math.max(1, Math.round(s / 60));
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remain = mins % 60;
  return remain > 0 ? `${hrs}h ${remain}m` : `${hrs}h`;
};

const RouteJobCard: React.FC<{
  job: RouteJob;
  onDismiss: () => void;
  onOpenRoute: () => void;
}> = ({ job, onDismiss, onOpenRoute }) => {
  // Synthetic in-stage tween so the bar visibly moves even though the
  // backend doesn't stream progress. Pulls toward the next stage's
  // anchor at 1% every 200ms once the job is past the very first frame.
  const [tween, setTween] = React.useState(0);
  React.useEffect(() => {
    if (job.stage === "completed" || job.stage === "failed" || job.stage === "completing") {
      setTween(0);
      return;
    }
    const id = setInterval(() => {
      setTween(prev => Math.min(prev + 0.5, 8));
    }, 200);
    return () => clearInterval(id);
  }, [job.stage]);

  // Reset the tween whenever the stage advances — the new anchor takes
  // over and the next 8% of headroom is reclaimed for the next stage.
  React.useEffect(() => {
    setTween(0);
  }, [job.stage]);

  const isDone = job.stage === "completed";
  const isFailed = job.stage === "failed";
  const isWorking = !isDone && !isFailed;

  const progress = Math.min(100, STAGE_ANCHOR[job.stage] + (isWorking ? tween : 0));

  const dist = formatDistance(job.stats?.distanceMeters);
  const dur = formatDuration(job.stats?.durationSeconds);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden w-[320px]">
      <div className="px-3 py-2.5 flex items-start gap-2.5">
        {/* Status icon */}
        <div className={cn(
          "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5",
          isDone ? "bg-emerald-50" : isFailed ? "bg-red-50" : "bg-primary/10",
        )}>
          {isDone ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          ) : isFailed ? (
            <AlertCircle className="w-4 h-4 text-red-600" />
          ) : (
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[12px] font-semibold text-gray-900 truncate">
              {job.routeName || "Route"}
            </p>
            <button
              type="button"
              onClick={onDismiss}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 -mr-1"
              aria-label="Dismiss"
              title={isWorking ? "Hide widget — optimization continues in background" : "Dismiss"}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-[11px] text-gray-500 truncate mt-0.5">{job.message}</p>

          {/* Progress bar */}
          {isWorking || job.stage === "completing" ? (
            <div className="mt-2 h-1 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-primary transition-[width] duration-200 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          ) : null}

          {/* Stats footer once done */}
          {isDone && (dist || dur) && (
            <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500">
              {dist && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5" />
                  {dist}
                </span>
              )}
              {dur && <span>{dur}</span>}
              {Number.isFinite(job.totalStops) && job.totalStops > 0 && (
                <span>{job.totalStops} stops</span>
              )}
            </div>
          )}

          {/* Open-route action when complete and not on the route page */}
          {isDone && job.routeId && (
            <button
              type="button"
              onClick={onOpenRoute}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
            >
              View route
              <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const RouteProgressWidget: React.FC<RouteProgressWidgetProps> = ({
  hasActiveCall,
  hasActiveSms,
}) => {
  const { activeRouteJobs, dismissRouteJob } = useRouteProgress();
  const router = useRouter();
  const pathname = usePathname();

  if (activeRouteJobs.length === 0) return null;

  // The UnifiedDialerWidget's minimized button sits at `bottom-4 right-4
  // z-50` and is ALWAYS visible. We need to stack ABOVE it so the dialer
  // never gets blocked. Dialer button is ~56px tall + 16px bottom margin,
  // so we need at least bottom-24 (96px) to clear it cleanly. When an
  // active call dock or SMS panel is also showing, those take more
  // vertical space and we slide higher.
  //
  // z-index is kept BELOW the dialer's z-50 (we're above it physically,
  // not in stacking) so when the dialer expands its 600px panel, it can
  // cover this widget instead of the agent losing access to the dialer.
  const bottomOffset =
    hasActiveCall && hasActiveSms ? "bottom-44"
    : hasActiveCall ? "bottom-36"
    : hasActiveSms ? "bottom-36"
    : "bottom-24";

  return (
    <div className={cn("fixed right-[calc(1rem+env(safe-area-inset-right,0px))] z-40 flex flex-col gap-2 pointer-events-none", bottomOffset)}>
      {activeRouteJobs.map((job) => (
        <div key={job.id} className="pointer-events-auto">
          <RouteJobCard
            job={job}
            onDismiss={() => dismissRouteJob(job.id)}
            onOpenRoute={() => {
              if (!job.routeId) return;
              // Only navigate if we're not already on the routes hub —
              // the routes page already opens the latest route inline.
              if (!pathname?.startsWith("/routes")) {
                router.push("/routes");
              }
            }}
          />
        </div>
      ))}
    </div>
  );
};
